import {
  appAccentResponseSchema,
  businessLogosResponseSchema,
  type AppAccentResponse,
  type BusinessLogosResponse,
  type SetAppAccentRequest,
  type SetActiveLogoRequest,
  type UploadBusinessLogoRequest,
} from "@teamna/shared";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * The business logo (Settings, Brand). Every place that shows the logo reads it from the account's
 * list of businesses (`GET /v1/me/businesses`), so each write refreshes that list too: the sidebar
 * changes the moment the save lands.
 */

const logosPath = (businessId: string) => `/owner/businesses/${businessId}/logos`;

/** GET /owner/businesses/:id/logos */
export function useBusinessLogos(businessId: string | null): UseQueryResult<BusinessLogosResponse> {
  return useQuery({
    queryKey: queryKeys.owner.logos(businessId ?? "none"),
    queryFn: () => api.get(logosPath(businessId!), businessLogosResponseSchema),
    enabled: Boolean(businessId),
  });
}

function useLogoWrite<Input>(
  businessId: string,
  write: (input: Input) => Promise<BusinessLogosResponse>,
): UseMutationResult<BusinessLogosResponse, unknown, Input> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (logos) => {
      queryClient.setQueryData(queryKeys.owner.logos(businessId), logos);
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses });
    },
  });
}

/** POST /owner/businesses/:id/logos. The new logo goes live at once. */
export function useUploadLogo(
  businessId: string,
): UseMutationResult<BusinessLogosResponse, unknown, UploadBusinessLogoRequest> {
  return useLogoWrite(businessId, (input: UploadBusinessLogoRequest) =>
    api.post(logosPath(businessId), input, businessLogosResponseSchema),
  );
}

/** PUT /owner/businesses/:id/logos/active. An earlier logo, or null for initials. */
export function useSetActiveLogo(
  businessId: string,
): UseMutationResult<BusinessLogosResponse, unknown, SetActiveLogoRequest> {
  return useLogoWrite(businessId, (input: SetActiveLogoRequest) =>
    api.put(`${logosPath(businessId)}/active`, input, businessLogosResponseSchema),
  );
}

/**
 * PUT /owner/businesses/:id/app-accent: the colour of the Teamna app, or null for Teamna blue
 * (mobile V2, slice 2.2). The value lives on the business row, so the list is read again.
 */
export function useSetAppAccent(
  businessId: string,
): UseMutationResult<AppAccentResponse, unknown, SetAppAccentRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetAppAccentRequest) =>
      api.put(`/owner/businesses/${businessId}/app-accent`, input, appAccentResponseSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.businesses }),
  });
}
